import { z } from "zod";

/** Supported AaaC schema versions (compiler accepts only these). */
export const SUPPORTED_SCHEMA_VERSIONS = ["aaac/0.1"] as const;

/** Pattern for schema version field: `aaac/x.y` */
export const SCHEMA_VERSION_PATTERN = /^aaac\/\d+\.\d+$/;

export const JSONSchemaRefSchema = z.union([
  z.object({ schema: z.record(z.string(), z.unknown()) }),
  z.object({ $ref: z.string() }),
]);

export const OptionSchema = z.object({
  name: z.string().min(1),
  schema: z.record(z.string(), z.unknown()),
  required: z.boolean().optional(),
  description: z.string().optional(),
});

export const ArtifactSlotValueSchema = z.union([
  z.string(),
  z.object({
    artifact: z.string(),
    direction: z.enum(["read", "write", "readwrite"]),
  }),
]);

export const OperationSchema = z
  .object({
    description: z.string().min(1),
    agentWorkflow: z.string().optional(),
    agentTask: z.string().optional(),
    handler: z.string().optional(),
    options: z.array(OptionSchema).optional(),
    input: JSONSchemaRefSchema.optional(),
    output: z.object({
      success: JSONSchemaRefSchema,
      failed: JSONSchemaRefSchema.optional(),
    }),
    artifactSlots: z.record(z.string(), ArtifactSlotValueSchema).optional(),
    riskLevel: z.enum(["low", "medium", "high"]).optional(),
    idempotent: z.boolean().optional(),
    requiresConfirmation: z.boolean().optional(),
    execution: z
      .object({
        timeoutMs: z.number().optional(),
        costCeilingUsd: z.number().optional(),
      })
      .optional(),
    memoryRef: z
      .object({
        input: z.boolean().optional(),
        output: z.boolean().optional(),
      })
      .optional(),
  })
  .superRefine((op, ctx) => {
    const dispatchCount = [op.agentWorkflow, op.agentTask, op.handler].filter(
      (v) => v !== undefined,
    ).length;

    if (dispatchCount === 0) {
      ctx.addIssue({
        code: "custom",
        message:
          "Exactly one of agentWorkflow, agentTask, or handler must be specified",
        path: ["agentWorkflow"],
      });
    } else if (dispatchCount > 1) {
      ctx.addIssue({
        code: "custom",
        message:
          "Only one of agentWorkflow, agentTask, or handler may be specified",
        path: ["agentWorkflow"],
      });
    }
  });

export const GovernanceSchema = z
  .object({
    guardrails: z.boolean().optional(),
    observability: z.boolean().optional(),
  })
  .optional();

/**
 * Reference to an external binding package (e.g. `@aaac/binding-cursor`).
 *
 * The core toolchain stays binding-agnostic: it only knows that a binding
 * exists, which package implements it, and the CLI command name to expose.
 * The `config` payload is opaque to the core and is validated by the binding
 * package itself at runtime (via its own `configSchema`).
 */
export const BindingRefSchema = z.object({
  /** npm package name implementing the binding (e.g. "@aaac/binding-cursor"). */
  package: z.string().min(1),
  /** CLI command name to expose (defaults to the binding id). */
  command: z.string().optional(),
  /** Binding-specific configuration; validated by the binding package. */
  config: z.record(z.string(), z.unknown()).default({}),
});

export const ComponentSchema = z
  .object({
    schema: z
      .string()
      .regex(SCHEMA_VERSION_PATTERN, 'schema must match pattern "aaac/x.y"'),
    info: z.object({
      id: z.string().min(1),
      version: z.string().min(1),
      description: z.string().optional(),
    }),
    implementation: z.string().optional(),
    embedded_dsl_dir: z.string().optional(),
    projections: z.array(
      z.enum(["library", "cli", "claude", "openai"]),
    ),
    governance: GovernanceSchema,
    bindings: z.record(z.string(), BindingRefSchema).optional(),
    operations: z.record(z.string(), OperationSchema),
  })
  .superRefine((component, ctx) => {
    const usesAgentic = Object.values(component.operations).some(
      (op) => op.agentWorkflow !== undefined || op.agentTask !== undefined,
    );

    if (usesAgentic && !component.implementation) {
      ctx.addIssue({
        code: "custom",
        message:
          "implementation is required when any operation uses agentWorkflow or agentTask",
        path: ["implementation"],
      });
    }
  });

export type ComponentDSL = z.infer<typeof ComponentSchema>;
export type GovernanceDSL = z.infer<typeof GovernanceSchema>;
export type BindingRefDSL = z.infer<typeof BindingRefSchema>;
export type OperationDSL = z.infer<typeof OperationSchema>;
export type OptionDSL = z.infer<typeof OptionSchema>;
export type JSONSchemaRefDSL = z.infer<typeof JSONSchemaRefSchema>;
